import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import _ from 'lodash';

import {getProducts, setAutocompleteProducts} from "../../../redux/actions/product";
import {getSubstancesByName, setSubstances} from "../../../redux/actions/substance";

import {
    Grid,
    Row,
    Col,
    FormGroup,
    FormControl
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";

import ProductGeneralAutosuggest from "../ProductGeneralAutosuggest/ProductGeneralAutosuggest.jsx";
import "./ProductSearchForm.css";

class ProductSearchForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            nom: '',
            DCI: '',
            familleTherapeutique: '',
            classesPharmacologiques: '',
            forme: '',
            dosage: '',
            searchTerms: [],
            lastSearchTimestamp: null,
            isSearching: false,
            searchCache: new Map(),
            isOfflineMode: false
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.searchOffline = this.searchOffline.bind(this);
        this.debounceGetProducts = _.debounce(this.handleSearch, 300);

        this.debounceGetSubstances = _.debounce((name) => {
            if (name && name.trim().length >= 2) {
                this.props.getSubstancesByName(name);
            }
        }, 300);
    }

    // méthode pour recherche hors ligne avec PouchDB
    async searchOffline(filters) {
        try {
            const { getAllProductsOffline } = await import('../../../helpers/offlineApi');
            this.setState({ isSearching: true });
            const results = await getAllProductsOffline(filters);


            if (results && results.length > 0) {
                // Dispatcher les résultats dans le store Redux
                this.props.dispatch({
                    type: 'SET_PRODUCTS',
                    payload: results.map(product => ({
                        ...product,
                        source: 'pouchdb',
                        searchedOffline: true
                    }))
                });

                if (this.props.showNotification) {
                    this.props.showNotification(
                        `🔍 ${results.length} médicament${results.length > 1 ? 's' : ''} trouvé${results.length > 1 ? 's' : ''} en mode hors ligne (PouchDB)`,
                        'success'
                    );
                }
            } else {
                this.props.dispatch({
                    type: 'SET_PRODUCTS',
                    payload: []
                });

                if (this.props.showNotification) {
                    this.props.showNotification(
                        '⚠️ Aucun médicament trouvé pour ces critères en mode hors ligne',
                        'warning'
                    );
                }
            }

            this.setState({ isSearching: false });
            return results;

        } catch (error) {
            this.setState({ isSearching: false });

            if (this.props.showNotification) {
                this.props.showNotification(
                    'Erreur lors de la recherche hors ligne dans PouchDB',
                    'danger'
                );
            }
            return [];
        }
    }

    handleSearch(filters) {
        const { user, offlineUser, offlineAuthDone } = this.props;
        const isOfflineMode = !!(offlineUser && offlineAuthDone && !user);


        if (isOfflineMode) {
            return this.searchOffline(filters);
        }
        const searchKey = JSON.stringify(filters);

        if (this.state.searchCache.has(searchKey)) {
            const cachedResults = this.state.searchCache.get(searchKey);
            this.props.setAutocompleteProducts(cachedResults);
            return;
        }

        if (filters.nom && filters.nom.length < 2) {
            this.props.setAutocompleteProducts([]);
            return;
        }

        try {
            this.props.getProducts(filters, true).then(() => {
                if (this.props.autocompleteProducts) {
                    // Mettre en cache
                    this.state.searchCache.set(searchKey, this.props.autocompleteProducts);

                    // Limiter la taille du cache
                    if (this.state.searchCache.size > 50) {
                        const firstKey = this.state.searchCache.keys().next().value;
                        this.state.searchCache.delete(firstKey);
                    }
                }
            });
        } catch (error) {
        }
    }

    setValue(val, fieldName) {
        let newState = {};
        newState[fieldName] = val || '';
        this.setState(newState, () => {

            if (fieldName === 'nom' && val && val.trim().length >= 2) {
                this.debounceGetProducts({
                    nom: val.trim(),
                    DCI: '',
                    familleTherapeutique: '',
                    classesPharmacologiques: '',
                    dosage: '',
                    forme: ''
                });
            } else if (fieldName === 'DCI' && val && val.trim().length >= 2) {
                this.debounceGetSubstances(val.trim());
            } else if (fieldName === 'forme' && val && val.trim().length >= 2) {
                this.debounceGetProducts({
                    nom: '',
                    DCI: '',
                    familleTherapeutique: '',
                    classesPharmacologiques: '',
                    dosage: '',
                    forme: val.trim()
                });
            } else if (fieldName === 'familleTherapeutique' && val && val.trim().length >= 2) {
                this.debounceGetProducts({
                    nom: '',
                    DCI: '',
                    familleTherapeutique: val.trim(),
                    classesPharmacologiques: '',
                    dosage: '',
                    forme: ''
                });
            }
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.searchTerms !== this.state.searchTerms && this.state.searchTerms.length > 0) {
            this.props.setAutocompleteProducts([]);
            this.props.setSubstances([]);
        }

        if (prevProps.products !== this.props.products && this.state.isSearching) {
            this.setState({ isSearching: false });
        }
    }

    resetForm() {
        this.setState({
            nom: '',
            DCI: '',
            familleTherapeutique: '',
            classesPharmacologiques: '',
            forme: '',
            dosage: '',
            searchTerms: [],
            lastSearchTimestamp: null,
            isSearching: false
        });

        this.props.setAutocompleteProducts([]);
        this.props.setSubstances([]);

        if (this.props.resetSearch) {
            this.props.resetSearch();
        }

        if (this.props.showNotification) {
            this.props.showNotification(
                "Formulaire réinitialisé.",
                "info"
            );
        }

        if (this.props.onSearchReset) {
            this.props.onSearchReset();
        }
    };

    formatSearchTerms = (filters) => {
        const fieldLabels = {
            nom: 'Nom commercial',
            DCI: 'DCI',
            familleTherapeutique: 'Famille thérapeutique',
            classesPharmacologiques: 'Classe pharmacologique',
            forme: 'Forme',
            dosage: 'Dosage'
        };

        return Object.entries(filters)
            .filter(([_, value]) => value && value.trim())
            .map(([key, value]) => ({
                field: fieldLabels[key] || key,
                value: value.trim()
            }));
    };

    onSubmit = async (event) => {
        event.preventDefault();

        const {
            nom,
            DCI,
            familleTherapeutique,
            classesPharmacologiques,
            forme,
            dosage
        } = this.state;
        const hasValidInput = [nom, DCI, familleTherapeutique, classesPharmacologiques, forme, dosage]
            .some(field => field && field.trim().length > 0);

        if (hasValidInput) {
            try {

                const filters = {};

                if (nom && nom.trim()) {
                    filters.nom = nom.trim();
                }
                if (DCI && DCI.trim()) {
                    filters.DCI = DCI.trim();
                }
                if (familleTherapeutique && familleTherapeutique.trim()) {
                    filters.familleTherapeutique = familleTherapeutique.trim();
                }
                if (classesPharmacologiques && classesPharmacologiques.trim()) {
                    filters.classesPharmacologiques = classesPharmacologiques.trim();
                }
                if (forme && forme.trim()) {
                    filters.forme = forme.trim();
                }
                if (dosage && dosage.trim()) {
                    filters.dosage = dosage.trim();
                }

                const formattedTerms = this.formatSearchTerms(filters);
                this.setState({
                    searchTerms: formattedTerms,
                    lastSearchTimestamp: new Date(),
                    isSearching: true
                });

                // Détecter le mode hors ligne pour la recherche principale avec debug
                const { user, offlineUser, offlineAuthDone } = this.props;
                const isOfflineMode = !!(offlineUser && offlineAuthDone && !user);

                if (isOfflineMode) {
                    await this.searchOffline(filters);
                } else {
                    await this.props.getProducts(filters, false);
                }

                if (this.props.onSearchComplete) {
                    this.props.onSearchComplete();
                }

                setTimeout(() => {
                    this.setState({
                        nom: '',
                        DCI: '',
                        familleTherapeutique: '',
                        classesPharmacologiques: '',
                        forme: '',
                        dosage: ''
                    });
                }, 500);

                // Notification de confirmation
                if (this.props.showNotification) {
                    const searchText = formattedTerms.map(term => `${term.field}: "${term.value}"`).join(', ');
                    const modeText = isOfflineMode ? ' (mode hors ligne)' : '';
                    this.props.showNotification(
                        `Recherche effectuée pour : ${searchText}${modeText}`,
                        "success"
                    );
                }

            } catch (error) {
                this.setState({ isSearching: false });
                if (this.props.showNotification) {
                    this.props.showNotification(
                        "Une erreur est survenue lors de la recherche.",
                        "danger"
                    );
                }
            }
        } else {
            if (this.props.showNotification) {
                this.props.showNotification(
                    "Veuillez remplir au moins un champ pour effectuer une recherche.",
                    "warning"
                );
            }
        }
    };

    renderSearchTerms = () => {
        const { searchTerms, lastSearchTimestamp, isSearching } = this.state;

        if (searchTerms.length === 0 && !isSearching) {
            return null;
        }
        if (isSearching) {
            return (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: 'var(--app-primary-tint)',
                    borderRadius: '8px',
                    border: '1px solid var(--app-primary-color)',
                    textAlign: 'center'
                }}>
                    <i className="fa fa-spinner fa-spin" style={{
                        fontSize: '20px',
                        color: 'var(--app-primary-color)',
                        marginRight: '10px'
                    }}></i>
                    <span style={{ color: 'var(--app-primary-color)', fontWeight: '600' }}>
                        Recherche en cours...
                    </span>
                </div>
            );
        }

        const formatTime = (date) => {
            if (!date) return '';
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);

            if (diffMins < 1) {
                return 'à l\'instant';
            } else if (diffMins === 1) {
                return 'il y a 1 minute';
            } else if (diffMins < 60) {
                return `il y a ${diffMins} minutes`;
            } else {
                const diffHours = Math.floor(diffMins / 60);
                if (diffHours === 1) {
                    return 'il y a 1 heure';
                } else {
                    return `il y a ${diffHours} heures`;
                }
            }
        };

        return (
            <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <i className="fa fa-history" style={{
                            color: 'var(--app-primary-color)',
                            fontSize: '18px'
                        }}></i>
                        <span style={{
                            fontWeight: '600',
                            color: '#495057',
                            fontSize: '16px'
                        }}>
                            Dernière recherche
                        </span>
                    </div>

                    {lastSearchTimestamp && (
                        <span style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            backgroundColor: '#e9ecef',
                            padding: '4px 10px',
                            borderRadius: '12px'
                        }}>
                            <i className="fa fa-clock" style={{ marginRight: '5px' }}></i>
                            {formatTime(lastSearchTimestamp)}
                        </span>
                    )}
                </div>

                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginTop: '10px'
                }}>
                    {searchTerms.map((term, index) => (
                        <div
                            key={`${term.field}-${index}`}
                            style={{
                                backgroundColor: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '20px',
                                padding: '8px 15px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                        >
                            <span style={{
                                fontSize: '12px',
                                color: '#6c757d',
                                fontWeight: '500',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {term.field}:
                            </span>
                            <span style={{
                                fontSize: '14px',
                                color: '#495057',
                                fontWeight: '600'
                            }}>
                                {term.value}
                            </span>
                            <div style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--app-primary-color)'
                            }}></div>
                        </div>
                    ))}
                </div>

                <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e9ecef',
                    textAlign: 'center'
                }}>
                    <span style={{
                        fontSize: '13px',
                        color: '#6c757d',
                        fontStyle: 'italic'
                    }}>
                        <i className="fa fa-info-circle" style={{ marginRight: '5px' }}></i>
                        {searchTerms.length} critère{searchTerms.length > 1 ? 's' : ''} de recherche appliqué{searchTerms.length > 1 ? 's' : ''}
                    </span>
                </div>
            </div>
        );
    };

    render() {
        const {
            nom,
            DCI,
            familleTherapeutique,
            classesPharmacologiques,
            forme,
            dosage,
            isSearching
        } = this.state;

        const isFormEmpty = !nom && !DCI && !familleTherapeutique &&
            !classesPharmacologiques && !forme && !dosage;
        const isOfflineMode = !!(this.props.offlineUser && this.props.offlineAuthDone && !this.props.user);
        const cardTitle = isOfflineMode
            ? "🔍 Recherche de Médicaments 📱 (Mode hors ligne - PouchDB)"
            : "🔍 Recherche de Médicaments";

        return (
            <div className="product-search-form">
                {isOfflineMode && (
                    <div style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '8px',
                        padding: '12px 15px',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <i className="fa fa-wifi" style={{
                            fontSize: '18px',
                            color: '#856404',
                            transform: 'rotate(45deg)'
                        }}></i>
                        <span style={{
                            color: '#856404',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            Mode hors ligne activé - Recherche dans la base de données locale (PouchDB)
                        </span>
                        <i className="fa fa-database" style={{
                            fontSize: '16px',
                            color: '#856404',
                            marginLeft: 'auto'
                        }}></i>
                    </div>
                )}

                <Card
                    title={cardTitle}
                    content={
                        <Grid fluid>
                            <form onSubmit={this.onSubmit}>
                                {/* Ligne 1: Champs principaux */}
                                <Row>
                                    <Col xs={12} md={4}>
                                        <FormGroup>
                                            <label style={{
                                                color: '#555',
                                                fontWeight: 'bold',
                                                marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            Nom Commercial
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={nom}
                                            onChange={(e) => this.setValue(e.target.value, 'nom')}
                                            addTag={(val) => this.setValue(val, 'nom')}
                                            suggestions={this.props.autocompleteProducts}
                                            placeholder="Ex: FARMODOXI, DOLIPRANE..."
                                            clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetProducts({nom: val.trim()}, true);
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="nom"
                                            pillColumns={['DCI', 'forme', 'dosage']}
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={12} md={4}>
                                    <FormGroup>
                                        <label style={{
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            DCI (Substance Active)
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={DCI}
                                            onChange={(e) => this.setValue(e.target.value, 'DCI')}
                                            addTag={(val) => {
                                                if (val && val.name) {
                                                    this.setValue(val.name, 'DCI');
                                                } else if (val && typeof val === 'object') {
                                                    this.setValue(val.name || val.dci || val.DCI || '', 'DCI');
                                                } else {
                                                    this.setValue(val, 'DCI');
                                                }
                                            }}
                                            suggestions={this.props.substances || []}
                                            placeholder="Ex: Paracétamol, Doxycycline..."
                                            clearSuggestions={() => this.props.setSubstances([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetSubstances(val.trim());
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="name"
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={12} md={4}>
                                    <FormGroup>
                                        <label style={{
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            Forme Pharmaceutique
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={forme}
                                            onChange={(e) => this.setValue(e.target.value, 'forme')}
                                            addTag={(val) => this.setValue(val, 'forme')}
                                            suggestions={this.props.autocompleteProducts}
                                            placeholder="Ex: Comprimé, Gélule, Sirop..."
                                            clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetProducts({forme: val.trim()}, true);
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="forme"
                                            pillColumns={['nom', 'dosage']}
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row style={{ marginTop: '15px' }}>
                                <Col xs={12} md={4}>
                                    <FormGroup>
                                        <label style={{
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            Famille Thérapeutique
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={familleTherapeutique}
                                            onChange={(e) => this.setValue(e.target.value, 'familleTherapeutique')}
                                            addTag={(val) => this.setValue(val, 'familleTherapeutique')}
                                            suggestions={this.props.autocompleteProducts}
                                            placeholder="Ex: Antibiotique, Analgésique..."
                                            clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetProducts({familleTherapeutique: val.trim()}, true);
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="familleTherapeutique"
                                            pillColumns={['nom', 'DCI']}
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={12} md={4}>
                                    <FormGroup>
                                        <label style={{
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            Classes Pharmacologiques
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={classesPharmacologiques}
                                            onChange={(e) => this.setValue(e.target.value, 'classesPharmacologiques')}
                                            addTag={(val) => this.setValue(val, 'classesPharmacologiques')}
                                            suggestions={this.props.autocompleteProducts}
                                            placeholder="Ex: AINS, Pénicilline..."
                                            clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetProducts({classesPharmacologiques: val.trim()}, true);
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="classesPharmacologiques"
                                            pillColumns={['nom', 'DCI']}
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={12} md={4}>
                                    <FormGroup>
                                        <label style={{
                                            color: '#555',
                                            fontWeight: 'bold',
                                            marginBottom: '8px',
                                            display: 'block'
                                        }}>
                                            Dosage
                                        </label>
                                        <ProductGeneralAutosuggest
                                            value={dosage}
                                            onChange={(e) => this.setValue(e.target.value, 'dosage')}
                                            addTag={(val) => this.setValue(val, 'dosage')}
                                            suggestions={this.props.autocompleteProducts}
                                            placeholder="Ex: 500mg, 100mg..."
                                            clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                            getSuggestions={(val) => {
                                                if (val && val.trim().length > 0) {
                                                    this.debounceGetProducts({dosage: val.trim()}, true);
                                                }
                                            }}
                                            style={{width: '100%'}}
                                            formControl
                                            nameColumn="dosage"
                                            pillColumns={['nom', 'forme']}
                                            disabled={isSearching}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Ligne 3: Boutons d'action */}
                            <Row style={{ marginTop: '25px', marginBottom: '10px' }}>
                                <Col xs={12}>
                                    <div style={{
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '15px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Button
                                            type="submit"
                                            bsStyle="info"
                                            bsSize="large"
                                            disabled={isSearching}
                                            className="product-search-submit"
                                            style={{
                                                padding: '12px 30px',
                                                fontSize: '15px',
                                                fontWeight: 'bold',
                                                borderRadius: '25px',
                                                background: 'var(--app-primary-color)',
                                                border: 'none',
                                                color: '#fff',
                                                boxShadow: '0 4px 15px rgba(var(--app-primary-rgb), 0.3)',
                                                transition: 'all 0.3s ease',
                                                minWidth: '200px',
                                                opacity: isSearching ? 0.6 : 1
                                            }}
                                        >
                                            {isSearching ? (
                                                <>
                                                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
                                                    Recherche...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-search" style={{ marginRight: '10px' }}></i>
                                                    Rechercher
                                                </>
                                            )}
                                        </Button>

                                        {!isFormEmpty && (
                                            <Button
                                                type="button"
                                                bsStyle="default"
                                                bsSize="large"
                                                onClick={this.resetForm}
                                                disabled={isSearching}
                                                style={{
                                                    padding: '12px 30px',
                                                    fontSize: '15px',
                                                    fontWeight: 'bold',
                                                    borderRadius: '25px',
                                                    boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                                                    transition: 'all 0.3s ease',
                                                    minWidth: '200px',
                                                    backgroundColor: '#f8f9fa',
                                                    color: '#495057',
                                                    border: '1px solid #dee2e6',
                                                    opacity: isSearching ? 0.6 : 1
                                                }}
                                            >
                                                <i className="fa fa-times" style={{ marginRight: '10px' }}></i>
                                                Effacer
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {this.renderSearchTerms()}
                        </form>
                    </Grid>
                }
            />
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        autocompleteProducts: state.products.autocompleteProducts,
        substances: state.substances.substances,
        products: state.products.products,
        // Ajouter l'état d'authentification hors ligne
        user: state.user.user,
        offlineUser: state.user.offlineUser,
        offlineAuthDone: state.user.offlineAuthDone
    }
};

const mapDispatchToProps = dispatch => {
    return {
        getProducts: (filters, autocompleteFetch) => {
            return dispatch(getProducts(filters, autocompleteFetch));
        },
        setAutocompleteProducts: (products) => {
            dispatch(setAutocompleteProducts(products));
        },
        getSubstancesByName: (name) => {
            dispatch(getSubstancesByName(name));
        },
        setSubstances: (substances) => {
            dispatch(setSubstances(substances));
        },
        dispatch
    }
};

ProductSearchForm.propTypes = {
    autocompleteProducts: PropTypes.array,
    substances: PropTypes.array,
    products: PropTypes.array,
    getProducts: PropTypes.func,
    setAutocompleteProducts: PropTypes.func,
    getSubstancesByName: PropTypes.func,
    setSubstances: PropTypes.func,
    showNotification: PropTypes.func,
    onSearchComplete: PropTypes.func,
    onSearchReset: PropTypes.func,
    resetSearch: PropTypes.func,
    user: PropTypes.object,
    offlineUser: PropTypes.object,
    offlineAuthDone: PropTypes.bool,
    dispatch: PropTypes.func,
};

ProductSearchForm.defaultProps = {
    showNotification: () => {},
    resetSearch: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSearchForm);